import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountCircleOutlined,
  InsightsOutlined,
  LinkOffOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  item: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
  },
  itemLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  itemValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemHint: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  picker: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const t = (key, options) => i18n.t(`settings.googleAds.account.${key}`, options);

const formatCustomerId = (id) =>
  String(id || "").replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3");

const accountKey = (account) => `${account.customerId}:${account.loginCustomerId}`;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : t("never"));

const GoogleAdsAccount = ({ onSynced }) => {
  const { classes } = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState("");
  const [picking, setPicking] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/google-ads/status");
      setStatus(data);
      return data;
    } catch (err) {
      toastError(err);
      return null;
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setAccountsError("");
    try {
      const { data } = await api.get("/google-ads/accounts");
      const list = Array.isArray(data) ? data : [];
      setAccounts(list);
      setSelected(list.length === 1 ? accountKey(list[0]) : "");
    } catch (err) {
      setAccounts([]);
      const message = err?.response?.data?.error || err?.message || "";
      setAccountsError(
        /insufficient authentication scopes/i.test(message)
          ? t("missingScope")
          : /not associated with any Ads accounts/i.test(message)
          ? t("notAdsUser")
          : message
      );
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const data = await loadStatus();
      if (data?.connected && !data?.customerId) {
        setPicking(true);
        loadAccounts();
      }
    })();
  }, [loadStatus, loadAccounts]);

  useEffect(() => {
    const result = searchParams.get("googleAds");
    if (!result) return;

    if (result === "ok") toast.success(t("connected"));
    else if (result === "missing_scope") toast.error(t("missingScope"), { duration: 10000 });
    else toast.error(t("connectError"));

    const next = new URLSearchParams(searchParams);
    next.delete("googleAds");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/google-ads/connect");
      window.location.href = data.url;
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };

  const handleChangeAccount = () => {
    setPicking(true);
    loadAccounts();
  };

  const handleSelectAccount = async () => {
    const account = accounts.find((a) => accountKey(a) === selected);
    if (!account) return;

    setBusy(true);
    try {
      const { data } = await api.put("/google-ads/account", {
        customerId: account.customerId,
        loginCustomerId: account.loginCustomerId,
      });
      toast.success(t("synced", { count: data?.campaigns ?? 0 }));
      setPicking(false);
      await loadStatus();
      if (onSynced) onSynced();
    } catch (err) {
      toastError(err);
      await loadStatus();
    }
    setBusy(false);
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/google-ads/sync");
      toast.success(t("synced", { count: data?.campaigns ?? 0 }));
      if (onSynced) onSynced();
    } catch (err) {
      toastError(err);
    }
    await loadStatus();
    setBusy(false);
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/google-ads/disconnect");
      toast.success(t("disconnected"));
      setAccounts([]);
      setPicking(false);
      await loadStatus();
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const renderBody = () => {
    if (!status) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!status.configured) {
      return <Alert severity="warning">{t("notConfigured")}</Alert>;
    }

    if (!status.connected) {
      return (
        <Box className={classes.root}>
          <Typography className={classes.itemHint}>{t("connectHint")}</Typography>
          <Box>
            <Button variant="contained" onClick={handleConnect} disabled={busy}>
              {t("connect")}
            </Button>
          </Box>
        </Box>
      );
    }

    if (picking || !status.customerId) {
      return (
        <Box className={classes.root}>
          <Typography className={classes.itemHint}>
            {t("pickHint", { email: status.email || "" })}
          </Typography>
          <Box className={classes.picker}>
            <TextField
              select
              size="small"
              fullWidth
              label={t("adAccount")}
              value={selected}
              disabled={loadingAccounts || busy}
              onChange={(e) => setSelected(e.target.value)}
              error={!!accountsError}
              helperText={
                loadingAccounts
                  ? t("loadingAccounts")
                  : accountsError
                  ? t("accountsError", { error: accountsError })
                  : accounts.length === 0
                  ? t("noAccounts")
                  : " "
              }
              slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            >
              <MenuItem value="" disabled>
                {t("pickPlaceholder")}
              </MenuItem>
              {accounts.map((a) => (
                <MenuItem key={accountKey(a)} value={accountKey(a)}>
                  {a.name} · {formatCustomerId(a.customerId)}
                  {a.currency ? ` · ${a.currency}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={handleSelectAccount}
              disabled={!selected || busy}
              sx={{ flexShrink: 0, height: 40 }}
            >
              {t("useAccount")}
            </Button>
            {status.customerId && (
              <Button onClick={() => setPicking(false)} disabled={busy} sx={{ flexShrink: 0, height: 40 }}>
                {t("cancel")}
              </Button>
            )}
          </Box>
          <Box className={classes.actions}>
            <Button onClick={loadAccounts} disabled={loadingAccounts || busy} startIcon={<SyncOutlined />}>
              {t("reloadAccounts")}
            </Button>
            <Button
              startIcon={<LinkOffOutlined />}
              color="inherit"
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}
            >
              {t("disconnectWith", { email: status.email || "Google" })}
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box className={classes.root}>
        {status.syncError && <Alert severity="error">{t("syncError", { error: status.syncError })}</Alert>}

        <Box className={classes.summary}>
          <Box className={classes.item}>
            <div className={classes.itemLabel}>{t("googleAccount")}</div>
            <div className={classes.itemValue}>{status.email || "—"}</div>
          </Box>
          <Box className={classes.item}>
            <div className={classes.itemLabel}>{t("adAccount")}</div>
            <div className={classes.itemValue}>{status.customerName || "—"}</div>
            <div className={classes.itemHint}>
              {formatCustomerId(status.customerId)}
              {status.currency ? ` · ${status.currency}` : ""}
            </div>
          </Box>
          <Box className={classes.item}>
            <div className={classes.itemLabel}>{t("lastSync")}</div>
            <div className={classes.itemValue}>{formatDate(status.lastSyncAt)}</div>
            <div className={classes.itemHint}>{t("autoSync")}</div>
          </Box>
        </Box>

        <Box className={classes.actions}>
          <Button
            startIcon={<LinkOffOutlined />}
            color="inherit"
            onClick={() => setConfirmDisconnect(true)}
            disabled={busy}
          >
            {t("disconnect")}
          </Button>
          <Button
            startIcon={<AccountCircleOutlined />}
            variant="outlined"
            onClick={handleChangeAccount}
            disabled={busy}
          >
            {t("changeAccount")}
          </Button>
          <Button
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SyncOutlined />}
            variant="contained"
            onClick={handleSync}
            disabled={busy}
          >
            {t("syncNow")}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <SettingsCard icon={InsightsOutlined} title={t("title")} subtitle={t("subtitle")}>
      {renderBody()}

      <ConfirmationModal
        title={t("disconnectTitle")}
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
      >
        {t("disconnectConfirm")}
      </ConfirmationModal>
    </SettingsCard>
  );
};

export default GoogleAdsAccount;
